/**
 * Network Pools API Service
 *
 * Service for managing network pools, which catalog available network resources
 * in Proxmox clusters (bridges, SDN VNETs, SDN zones).
 */

import type {
  NetworkPool,
  NetworkPoolCreate,
  NetworkPoolUpdate,
  NetworkPoolAuditLog,
  ProductNetworkPool,
  NetworkTopology,
  BulkImportFromProxmox,
  BulkImportResult,
} from '../types/network'
import { apiClient } from './api'

export const networkPoolsApi = {
  // ========================================
  // CRUD Operations
  // ========================================

  /**
   * List all network pools (optionally filtered)
   */
  async list(filters?: {
    cluster_id?: string
    network_type?: string
    enabled?: boolean
  }): Promise<{ total: number; pools: NetworkPool[]; cluster_id?: string }> {
    const params: any = {}

    if (filters?.cluster_id) {
      params.cluster_id = filters.cluster_id
    }
    if (filters?.network_type) {
      params.network_type = filters.network_type
    }
    if (filters?.enabled !== undefined) {
      params.enabled = filters.enabled
    }

    const response = await apiClient.get<{
      total: number
      pools: NetworkPool[]
      cluster_id?: string
    }>('/api/v1/network-pools/', { params })
    return response
  },

  /**
   * List network pools for a specific cluster
   */
  async listByCluster(
    clusterId: string,
  ): Promise<{ total: number; pools: NetworkPool[]; cluster_id: string }> {
    return await apiClient.get<{ total: number; pools: NetworkPool[]; cluster_id: string }>(
      `/api/v1/network-pools/cluster/${clusterId}`,
    )
  },

  /**
   * Get a specific network pool by ID
   */
  async get(poolId: string): Promise<NetworkPool> {
    return await apiClient.get<NetworkPool>(`/api/v1/network-pools/${poolId}`)
  },

  /**
   * Create a new network pool
   */
  async create(data: NetworkPoolCreate): Promise<NetworkPool> {
    return await apiClient.post<NetworkPool>('/api/v1/network-pools/', data)
  },

  /**
   * Update an existing network pool
   */
  async update(poolId: string, data: NetworkPoolUpdate): Promise<NetworkPool> {
    return await apiClient.put<NetworkPool>(`/api/v1/network-pools/${poolId}`, data)
  },

  /**
   * Delete a network pool
   */
  async delete(poolId: string): Promise<void> {
    await apiClient.delete(`/api/v1/network-pools/${poolId}`)
  },

  // ========================================
  // Bulk Operations
  // ========================================

  /**
   * Bulk import network resources from Proxmox
   * Automatically discovers bridges, SDN VNETs, and SDN zones
   */
  async bulkImportFromProxmox(data: BulkImportFromProxmox): Promise<BulkImportResult> {
    return await apiClient.post<BulkImportResult>(
      '/api/v1/network-pools/bulk/create-from-proxmox',
      data,
    )
  },

  // ========================================
  // Product Associations
  // ========================================

  /**
   * List product associations for a network pool
   */
  async listProductAssociations(poolId: string): Promise<ProductNetworkPool[]> {
    return await apiClient.get<ProductNetworkPool[]>(`/api/v1/network-pools/${poolId}/products`)
  },

  /**
   * Associate a product with a network pool
   */
  async associateProduct(
    poolId: string,
    productId: string,
    priority: number = 0,
    isDefault: boolean = false,
  ): Promise<ProductNetworkPool> {
    return await apiClient.post<ProductNetworkPool>(`/api/v1/network-pools/${poolId}/products`, {
      product_id: productId,
      priority,
      is_default: isDefault,
    })
  },

  /**
   * Update product association priority/default
   */
  async updateProductAssociation(
    poolId: string,
    productId: string,
    priority?: number,
    isDefault?: boolean,
  ): Promise<ProductNetworkPool> {
    return await apiClient.put<ProductNetworkPool>(
      `/api/v1/network-pools/${poolId}/products/${productId}`,
      {
        priority,
        is_default: isDefault,
      },
    )
  },

  /**
   * Remove product association from network pool
   */
  async removeProductAssociation(poolId: string, productId: string): Promise<void> {
    await apiClient.delete(`/api/v1/network-pools/${poolId}/products/${productId}`)
  },

  // ========================================
  // Audit & History
  // ========================================

  /**
   * Get audit history for a network pool
   */
  async getHistory(poolId: string): Promise<NetworkPoolAuditLog[]> {
    return await apiClient.get<NetworkPoolAuditLog[]>(`/api/v1/network-pools/${poolId}/history`)
  },

  // ========================================
  // Network Topology
  // ========================================

  /**
   * Get network topology for visualization (react-flow compatible)
   * For Cloud Provider use - requires cluster_id
   */
  async getTopology(clusterId: string): Promise<NetworkTopology> {
    return await apiClient.get<NetworkTopology>(`/api/v1/network-pools/topology/${clusterId}`)
  },

  /**
   * Get network topology for End Customer visualization
   * Returns customer's VMs and their network connections
   * No cluster_id needed - uses authenticated user's organization
   */
  async getCustomerTopology(): Promise<{
    customer_id: string
    customer_name: string
    nodes: {
      id: string
      type: string
      label: string
      data: Record<string, any>
      position?: { x: number; y: number }
    }[]
    edges: {
      id: string
      source: string
      target: string
      type: string
      animated: boolean
      label?: string
    }[]
    stats: {
      total_vms: number
      running_vms: number
      stopped_vms: number
      total_networks: number
      bridge_networks: number
      sdn_networks: number
    }
  }> {
    return await apiClient.get('/api/v1/end-customer/topology')
  },

  // ========================================
  // Statistics & Metrics
  // ========================================

  /**
   * Get network pool statistics
   */
  async getStats(poolId: string): Promise<{
    total_allocations: number
    max_allocations: number
    utilization_percentage: number
    enabled: boolean
    subnet_count?: number
    total_ips?: number
    allocated_ips?: number
  }> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/stats`)
  },

  // ========================================
  // Validation
  // ========================================

  /**
   * Check if a network identifier is already in use
   */
  async checkIdentifierExists(
    clusterId: string,
    networkIdentifier: string,
  ): Promise<{ exists: boolean; pool_id?: string }> {
    return await apiClient.get('/api/v1/network-pools/check-identifier', {
      params: { cluster_id: clusterId, network_identifier: networkIdentifier },
    })
  },

  // ========================================
  // SDN Sync Operations
  // ========================================

  /**
   * Synchronize SDN zones from Proxmox to Network Pools
   *
   * Discovers all SDN zones in the Proxmox cluster and creates/updates
   * network pools correspondingly.
   *
   * @param clusterId - Cluster UUID
   * @param overwriteManual - If true, updates manually created pools
   * @returns Sync result with created/updated/skipped pools
   */
  async syncFromSDN(
    clusterId: string,
    overwriteManual: boolean = false,
  ): Promise<{
    cluster_id: string
    cluster_name: string
    summary: {
      total_sdn_zones: number
      created: number
      updated: number
      skipped: number
      errors: number
    }
    created_pools: NetworkPool[]
    updated_pools: NetworkPool[]
    skipped_zones: { zone_id?: string; pool_id?: string; reason: string }[]
    errors: { zone_id?: string; error: string }[]
  }> {
    return await apiClient.post(`/api/v1/network-pools/sync-from-sdn/${clusterId}`, null, {
      params: { overwrite_manual: overwriteManual },
    })
  },

  /**
   * Get SDN source information for a network pool
   *
   * Returns metadata about the SDN zone this pool was synced from,
   * including sync status and Proxmox data.
   *
   * @param poolId - Network pool UUID
   * @returns SDN source metadata
   */
  async getSDNSourceInfo(poolId: string): Promise<{
    synced_from_sdn: boolean
    manual_pool?: boolean
    sdn_zone_id?: string
    sdn_zone_type?: string
    last_sync_at?: string
    proxmox_data?: any
  }> {
    return await apiClient.get(`/api/v1/network-pools/${poolId}/sdn-source`)
  },

  /**
   * Unified network discovery: Bridges + SDN Zones + VNETs
   *
   * This is the recommended endpoint for initial cluster setup.
   * Discovers and creates network pools for all network resources:
   * - Linux bridges (vmbr0, vmbr1, etc)
   * - SDN zones (if SDN is enabled)
   * - SDN VNETs (if SDN is enabled)
   *
   * @param clusterId - Cluster UUID
   * @returns Discovery report with all networks
   */
  async discoverAll(clusterId: string): Promise<{
    cluster_id: string
    cluster_name: string
    bridges: NetworkPool[]
    sdn_zones: {
      summary?: {
        total_sdn_zones: number
        created: number
        updated: number
        skipped: number
        errors: number
      }
      created_pools?: NetworkPool[]
      updated_pools?: NetworkPool[]
      skipped_zones?: { zone_id?: string; reason: string }[]
      errors?: { zone_id?: string; error: string }[]
    }
    vnets: NetworkPool[]
    bridges_error?: string
    sdn_zones_error?: string
    vnets_error?: string
  }> {
    return await apiClient.post(`/api/v1/network-pools/discover-all/${clusterId}`)
  },

  /**
   * Get all available networks (bridges, SDN zones, VNETs) from Proxmox for a cluster.
   * This is a unified endpoint that fetches all network types in a single request.
   * Returns networks WITHOUT creating pools - read-only operation for dropdown population.
   */
  async getAvailableNetworks(clusterId: string): Promise<AvailableNetworks> {
    const response = await apiClient.get<AvailableNetworks>(
      `/api/v1/network-pools/available-networks/${clusterId}`,
    )
    return response
  },
}

// ========================================
// Helper Functions
// ========================================

export const getNetworkTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    bridge: 'Bridge (vmbr)',
    sdn_vnet: 'SDN VNet',
  }
  return labels[type] || type
}

export const getNetworkTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    bridge: 'blue',
    sdn_vnet: 'purple',
  }
  return colors[type] || 'gray'
}

export const getNetworkTypeIconBg = (type: string): string => {
  const colors: Record<string, string> = {
    bridge: 'bg-blue-100',
    sdn_vnet: 'bg-purple-100',
  }
  return colors[type] || 'bg-gray-100'
}

export const getNetworkTypeIconColor = (type: string): string => {
  const colors: Record<string, string> = {
    bridge: 'text-blue-600',
    sdn_vnet: 'text-purple-600',
  }
  return colors[type] || 'text-gray-600'
}

export const getNetworkTypeBadgeBg = (type: string): string => {
  const colors: Record<string, string> = {
    bridge: 'bg-blue-100',
    sdn_vnet: 'bg-purple-100',
  }
  return colors[type] || 'bg-gray-100'
}

export const getNetworkTypeBadgeText = (type: string): string => {
  const colors: Record<string, string> = {
    bridge: 'text-blue-700',
    sdn_vnet: 'text-purple-700',
  }
  return colors[type] || 'text-gray-700'
}

export const formatAllocationStatus = (pool: NetworkPool): string => {
  if (!pool.max_allocations) {
    return `${pool.current_allocations} (ilimitado)`
  }
  return `${pool.current_allocations} / ${pool.max_allocations}`
}

export const getAllocationPercentage = (pool: NetworkPool): number => {
  if (!pool.max_allocations) return 0
  return (pool.current_allocations / pool.max_allocations) * 100
}

export const isPoolNearCapacity = (pool: NetworkPool, threshold: number = 80): boolean => {
  if (!pool.max_allocations) return false
  const percentage = getAllocationPercentage(pool)
  return percentage >= threshold
}

/**
 * Get available bridges from Proxmox for a cluster
 */
export async function getAvailableBridges(clusterId: string): Promise<string[]> {
  try {
    const response = await apiClient.get<{ bridges: string[] }>(
      `/api/v1/clusters/${clusterId}/proxmox/bridges`,
    )
    return response.bridges
  } catch (error) {
    console.error('Failed to fetch bridges:', error)
    return []
  }
}

/**
 * Get available SDN VNETs from Proxmox for a cluster
 */
export async function getAvailableVNETs(clusterId: string): Promise<string[]> {
  try {
    const response = await apiClient.get<{ vnets: any[] }>(
      `/api/v1/sdn/clusters/${clusterId}/vnets`,
    )
    return response.vnets?.map((vnet: any) => vnet.vnet) || []
  } catch (error) {
    console.error('Failed to fetch VNETs:', error)
    return []
  }
}

/**
 * Get available SDN Zones from Proxmox for a cluster
 */
export async function getAvailableZones(clusterId: string): Promise<string[]> {
  try {
    const response = await apiClient.get<{ zones: any[] }>(
      `/api/v1/sdn/clusters/${clusterId}/zones`,
    )
    return response.zones?.map((zone: any) => zone.zone) || []
  } catch (error) {
    console.error('Failed to fetch zones:', error)
    return []
  }
}

/**
 * Interfaces for available networks from Proxmox.
 * Used by the unified endpoint that fetches all network types in a single request.
 */
export type AvailableNetwork = {
  id: string
  name: string
  type?: string
  zone?: string
  node?: string
  active?: boolean
  address?: string
  gateway?: string
  tag?: number
  alias?: string
  pending?: boolean
}

export type AvailableNetworks = {
  cluster_id: string
  cluster_name: string
  bridges: AvailableNetwork[]
  sdn_zones: AvailableNetwork[]
  vnets: AvailableNetwork[]
  sdn_enabled: boolean
}
